import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getCities,
  createCity,
  updateCity,
  type City,
} from '../../api/admin';
import { PlacesAutocomplete, type PlaceResult } from '../events/PlacesAutocomplete';

interface CityManagementDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onCityUpdated: () => void;
}

const TIMEZONE_OPTIONS = [
  // US zones
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  // Canadian zones
  { value: 'America/Vancouver', label: 'Pacific — Vancouver (PT)' },
  { value: 'America/Edmonton', label: 'Mountain — Edmonton (MT)' },
  { value: 'America/Winnipeg', label: 'Central — Winnipeg (CT)' },
  { value: 'America/Toronto', label: 'Eastern — Toronto (ET)' },
  { value: 'America/Halifax', label: 'Atlantic — Halifax (AT)' },
  { value: 'America/St_Johns', label: 'Newfoundland — St. John\'s (NST)' },
];

/** Guess a sensible default timezone from a country code + state/province abbreviation. */
function guessTimezone(country: string | undefined, state: string | undefined): string {
  if (!country || !state) return 'America/New_York';

  if (country === 'CA') {
    // Canadian province codes → IANA zone (covers the major population centres)
    const caZones: Record<string, string> = {
      BC: 'America/Vancouver',
      AB: 'America/Edmonton',
      SK: 'America/Regina',
      MB: 'America/Winnipeg',
      ON: 'America/Toronto',
      QC: 'America/Toronto',
      NB: 'America/Halifax',
      NS: 'America/Halifax',
      PE: 'America/Halifax',
      NL: 'America/St_Johns',
      YT: 'America/Whitehorse',
      NT: 'America/Yellowknife',
      NU: 'America/Rankin_Inlet',
    };
    return caZones[state.toUpperCase()] ?? 'America/Toronto';
  }

  // US state abbreviation → IANA zone (rough default; admin can adjust)
  const usZones: Record<string, string> = {
    AK: 'America/Anchorage',
    HI: 'Pacific/Honolulu',
    AZ: 'America/Phoenix',
    CA: 'America/Los_Angeles',
    NV: 'America/Los_Angeles',
    OR: 'America/Los_Angeles',
    WA: 'America/Los_Angeles',
    CO: 'America/Denver',
    ID: 'America/Denver',
    MT: 'America/Denver',
    NM: 'America/Denver',
    UT: 'America/Denver',
    WY: 'America/Denver',
  };
  return usZones[state.toUpperCase()] ?? 'America/New_York';
}

interface PickedPlace {
  name: string;
  state: string;
  country: string;
  placeId: string;
  lat: number;
  lng: number;
}

export default function CityManagementDrawer({
  isOpen,
  onClose,
  onCityUpdated,
}: CityManagementDrawerProps) {
  const [cities, setCities] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // ---- Add form state ----
  const [isAdding, setIsAdding] = useState(false);
  const [addCityText, setAddCityText] = useState('');
  const [addPicked, setAddPicked] = useState<PickedPlace | null>(null);
  const [addTimezone, setAddTimezone] = useState('America/New_York');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // ---- Edit form state ----
  const [editingCityId, setEditingCityId] = useState<string | null>(null);
  const [editCityText, setEditCityText] = useState('');
  const [editPicked, setEditPicked] = useState<PickedPlace | null>(null);
  const [editTimezone, setEditTimezone] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const previousOverflow = useRef<string>('');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const citiesData = await getCities();
      setCities(citiesData);
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load cities');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement;
      previousOverflow.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      loadData();
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = previousOverflow.current;
      setSearchQuery('');
      setEditingCityId(null);
      setSaveError(null);
      setIsAdding(false);
      setAddCityText('');
      setAddPicked(null);
      setCreateError(null);
      triggerRef.current?.focus();
    }

    return () => {
      document.body.style.overflow = previousOverflow.current;
    };
  }, [isOpen, loadData]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editingCityId) {
          e.stopPropagation();
          setEditingCityId(null);
          setSaveError(null);
        } else if (isAdding) {
          e.stopPropagation();
          setIsAdding(false);
          setAddCityText('');
          setAddPicked(null);
          setCreateError(null);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, editingCityId, isAdding, onClose]);

  const filteredCities = cities
    .filter((city) => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        city.name.toLowerCase().includes(query) ||
        city.state.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // ---- Add flow ----

  const handleAddPlaceSelected = (place: PlaceResult) => {
    const name = place.addressComponents?.city || place.name || '';
    const state = place.addressComponents?.state || '';
    const country = place.addressComponents?.country || 'US';
    const placeId = place.placeId || '';
    const picked: PickedPlace = { name, state, country, placeId, lat: place.lat, lng: place.lng };
    setAddPicked(picked);
    setAddTimezone(guessTimezone(country, state));
    // Display "City, STATE" in the input as confirmation
    setAddCityText(state ? `${name}, ${state}` : name);
    setCreateError(null);
  };

  const handleCreate = async () => {
    if (!addPicked?.name || !addPicked.state || !addPicked.placeId) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      await createCity({
        name: addPicked.name,
        state: addPicked.state,
        timezone: addTimezone,
        google_place_id: addPicked.placeId,
        country: addPicked.country,
        latitude: addPicked.lat,
        longitude: addPicked.lng,
      });
      setIsAdding(false);
      setAddCityText('');
      setAddPicked(null);
      await loadData();
      onCityUpdated();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create city');
    } finally {
      setIsCreating(false);
    }
  };

  const cancelAdd = () => {
    setIsAdding(false);
    setAddCityText('');
    setAddPicked(null);
    setCreateError(null);
  };

  // ---- Edit flow ----

  const startEdit = (city: City) => {
    setEditingCityId(city.id);
    // Pre-fill the city name so the admin can see what's currently stored.
    // The PlacesAutocomplete input starts with the city name as the initial text.
    setEditCityText(city.name);
    setEditPicked(null); // no picked place yet — admin must re-pick to change identity
    setEditTimezone(city.timezone || 'America/New_York');
    setSaveError(null);
  };

  const cancelEdit = () => {
    setEditingCityId(null);
    setSaveError(null);
  };

  const handleEditPlaceSelected = (place: PlaceResult) => {
    const name = place.addressComponents?.city || place.name || '';
    const state = place.addressComponents?.state || '';
    const country = place.addressComponents?.country || 'US';
    const placeId = place.placeId || '';
    const picked: PickedPlace = { name, state, country, placeId, lat: place.lat, lng: place.lng };
    setEditPicked(picked);
    setEditTimezone(guessTimezone(country, state));
    setEditCityText(state ? `${name}, ${state}` : name);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editingCityId) return;
    // Either a new place was picked (editPicked set), or the admin only changed
    // the timezone (editPicked null — keep existing name/state/place_id).
    if (!editPicked && !editTimezone) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const payload = editPicked
        ? {
            name: editPicked.name,
            state: editPicked.state,
            timezone: editTimezone,
            google_place_id: editPicked.placeId,
            country: editPicked.country,
            latitude: editPicked.lat,
            longitude: editPicked.lng,
          }
        : { timezone: editTimezone };
      await updateCity(editingCityId, payload);
      setEditingCityId(null);
      await loadData();
      onCityUpdated();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save city');
    } finally {
      setIsSaving(false);
    }
  };

  const getTimezoneOptions = (currentTimezone: string) => {
    const options = [...TIMEZONE_OPTIONS];
    if (currentTimezone && !options.some(tz => tz.value === currentTimezone)) {
      options.push({ value: currentTimezone, label: currentTimezone });
    }
    return options;
  };

  const inputCls = 'w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm';

  return (
    <div
      className={`fixed inset-0 z-50 ${isOpen ? '' : 'pointer-events-none'}`}
    >
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-96 bg-white shadow-xl z-50 transform transition-transform duration-300 flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-warm-200">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-warm-900">Manage Cities</h2>
            {!isLoading && !fetchError && (
              <span className="text-xs bg-warm-100 text-warm-600 px-2 py-0.5 rounded-full">
                {filteredCities.length}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-warm-400 hover:text-warm-600 rounded transition-colors"
            aria-label="Close drawer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search + Add button */}
        <div className="px-4 py-3 border-b border-warm-200 sticky top-0 bg-white space-y-2">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search cities..."
            className={inputCls}
          />
          {!isAdding && (
            <button
              type="button"
              onClick={() => { setIsAdding(true); setCreateError(null); }}
              className="w-full px-4 py-2 border border-dashed border-warm-300 rounded-lg text-sm text-warm-600 hover:border-primary-400 hover:text-primary-600 transition-colors"
            >
              + Add City
            </button>
          )}
        </div>

        {/* Add City form */}
        {isAdding && (
          <div className="px-4 py-4 border-b border-warm-200 bg-primary-50 space-y-3">
            <p className="text-xs font-semibold text-primary-700 uppercase tracking-wide">Add City</p>
            <div>
              <label className="block text-xs font-medium text-warm-600 mb-1">City *</label>
              <PlacesAutocomplete
                mode="city"
                value={addCityText}
                onInputChange={(text) => { setAddCityText(text); setAddPicked(null); setCreateError(null); }}
                onPlaceSelected={handleAddPlaceSelected}
                placeholder="Search for a city…"
                className={inputCls}
              />
              {addPicked && (
                <p className="mt-1 text-xs text-warm-500">
                  {addPicked.name}, {addPicked.state} — {addPicked.country}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-warm-600 mb-1">Timezone</label>
              <select
                value={addTimezone}
                onChange={(e) => setAddTimezone(e.target.value)}
                className={`${inputCls} bg-white`}
              >
                {getTimezoneOptions(addTimezone).map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>

            {createError && (
              <p className="text-xs text-danger-600">{createError}</p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={cancelAdd}
                disabled={isCreating}
                className="px-4 py-2.5 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={isCreating || !addPicked?.placeId}
                className="px-4 py-2.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-medium"
              >
                {isCreating ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          )}

          {fetchError && (
            <div className="p-4 text-center">
              <p className="text-sm text-danger-600 mb-3">{fetchError}</p>
              <button
                type="button"
                onClick={loadData}
                className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !fetchError && filteredCities.length === 0 && (
            <div className="p-4 text-center">
              <p className="text-sm text-warm-500">No cities found</p>
            </div>
          )}

          {!isLoading && !fetchError && filteredCities.map((city) => {
            const isEditing = editingCityId === city.id;
            const timezoneOptions = isEditing ? getTimezoneOptions(editTimezone) : [];

            return (
              <div key={city.id} className="border-b border-warm-100">
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-warm-900 truncate">{city.name}</p>
                    <p className="text-xs text-warm-400 mt-0.5">{city.state}</p>
                  </div>
                  {!isEditing && (
                    <button
                      type="button"
                      onClick={() => startEdit(city)}
                      className="ml-2 p-1.5 text-warm-400 hover:text-primary-600 rounded transition-colors flex-shrink-0"
                      aria-label={`Edit ${city.name}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </div>

                {isEditing && (
                  <div className="px-4 pb-4 space-y-3 bg-warm-50">
                    <div>
                      <label className="block text-xs font-medium text-warm-600 mb-1">
                        City *
                        <span className="ml-1 font-normal text-warm-400">(search to re-pick)</span>
                      </label>
                      <PlacesAutocomplete
                        mode="city"
                        value={editCityText}
                        onInputChange={(text) => { setEditCityText(text); setEditPicked(null); setSaveError(null); }}
                        onPlaceSelected={handleEditPlaceSelected}
                        placeholder="Search for a city…"
                        className={inputCls}
                      />
                      {editPicked && (
                        <p className="mt-1 text-xs text-warm-500">
                          {editPicked.name}, {editPicked.state} — {editPicked.country}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-warm-600 mb-1">Timezone</label>
                      <select
                        value={editTimezone}
                        onChange={(e) => { setEditTimezone(e.target.value); setSaveError(null); }}
                        className={`${inputCls} bg-white`}
                      >
                        {timezoneOptions.map(tz => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </select>
                    </div>

                    {saveError && (
                      <p className="text-xs text-danger-600">{saveError}</p>
                    )}

                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        disabled={isSaving}
                        className="px-4 py-2.5 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors text-sm font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-2.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-medium"
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
